// Navbar.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logoMark from '../assets/teamkitty-mark.svg';

function inferRoleFromToken() {
  try {
    const idToken = localStorage.getItem('beercard_id_token') || localStorage.getItem('beercard_token');
    if (!idToken) return null;

    const [, payload] = idToken.split('.');
    const claims = JSON.parse(atob(payload));
    const groups = claims['cognito:groups'] || [];

    if (groups.includes('superadmin')) return 'superadmin';
    if (groups.includes('admin')) return 'admin';
    return 'player';
  } catch {
    return null;
  }
}

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [role, setRole] = useState(localStorage.getItem('beercard_role'));
  const navigate = useNavigate();
  const location = useLocation();

  const hiddenPaths = ['/login', '/register', '/confirm'];

  useEffect(() => {
    if (!role) {
      const inferred = inferRoleFromToken();
      if (inferred) {
        localStorage.setItem('beercard_role', inferred);
        setRole(inferred);
      }
    }
  }, [role]);

  if (hiddenPaths.includes(location.pathname)) {
    return null;
  }

  const isSuperAdmin = role === 'superadmin';
  const isAdmin = role === 'admin';

  const goTo = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleLogoClick = () => {
    if (isSuperAdmin) return goTo('/superadmin');
    if (isAdmin) return goTo('/admin');
    return goTo('/player');
  };

  const handleLogout = () => {
    localStorage.clear();
    setMenuOpen(false);
    navigate('/login');
  };

  const navLinkStyle = (path) =>
    `px-3 py-2 rounded-md text-sm font-semibold transition ${
      location.pathname === path
        ? 'bg-[#0B1C2C] text-white'
        : 'text-[#0B1C2C] dark:text-white hover:bg-[#FFF7E6] dark:hover:bg-gray-700'
    }`;

  return (
    <>
      <nav className="bg-white dark:bg-gray-900 text-black dark:text-white shadow-md sticky top-0 z-50 border-b border-[#0B1C2C]/10">
        <div className="max-w-6xl mx-auto px-4 py-2 md:py-3 flex justify-between items-center gap-4">
          <button
            type="button"
            className="flex items-center gap-2 md:gap-3 cursor-pointer min-w-0 shrink-0"
            onClick={handleLogoClick}
            aria-label="Go to TeamKitty dashboard"
          >
            <img
              src={logoMark}
              alt="TeamKitty"
              className="h-8 w-8 md:h-10 md:w-10 lg:h-11 lg:w-11 object-contain rounded-xl shrink-0"
            />

            <span className="hidden sm:inline text-lg md:text-xl tk-wordmark leading-none whitespace-nowrap">
              <span className="tk-wordmark-team">team</span>
              <span className="tk-wordmark-kitty">kitty</span>
              <span className="tk-wordmark-io">.io</span>
            </span>
          </button>

          <div className="sm:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-[#0B1C2C] dark:text-white focus:outline-none p-2 rounded-md hover:bg-[#FFF7E6] dark:hover:bg-gray-700"
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          <div className="hidden sm:flex items-center gap-2 md:gap-4 flex-wrap justify-end">
            {isSuperAdmin && (
              <button type="button" onClick={() => goTo('/superadmin')} className={navLinkStyle('/superadmin')}>
                Super Admin
              </button>
            )}

            {isAdmin && (
              <>
                <button type="button" onClick={() => goTo('/admin')} className={navLinkStyle('/admin')}>
                  Admin
                </button>
                <button type="button" onClick={() => goTo('/leaderboard')} className={navLinkStyle('/leaderboard')}>
                  Leaderboard
                </button>
              </>
            )}

            {!isSuperAdmin && (
              <>
                <button type="button" onClick={() => goTo('/player')} className={navLinkStyle('/player')}>
                  My Card
                </button>
                <button type="button" onClick={() => goTo('/topup')} className={navLinkStyle('/topup')}>
                  Top-Up
                </button>
                <button type="button" onClick={() => goTo('/noticeboard')} className={navLinkStyle('/noticeboard')}>
                  Noticeboard
                </button>
                <button type="button" onClick={() => goTo('/collections')} className={navLinkStyle('/collections')}>
                  Collections
                </button>
              </>
            )}

            <button
              type="button"
              onClick={() => goTo('/delete-account')}
              className={navLinkStyle('/delete-account')}
            >
              Account
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="bg-[#F59E0B] hover:bg-[#E58F00] text-[#0B1C2C] px-3 py-2 rounded-md text-sm font-bold transition"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div className="sm:hidden bg-white dark:bg-gray-900 px-4 pb-4 shadow-md space-y-2 border-b border-[#0B1C2C]/10">
          {isAdmin && (
            <>
              <button type="button" onClick={() => goTo('/admin')} className={`${navLinkStyle('/admin')} block w-full text-left`}>
                Admin
              </button>
              <button type="button" onClick={() => goTo('/leaderboard')} className={`${navLinkStyle('/leaderboard')} block w-full text-left`}>
                Leaderboard
              </button>
            </>
          )}

          {!isSuperAdmin && (
            <>
              <button type="button" onClick={() => goTo('/player')} className={`${navLinkStyle('/player')} block w-full text-left`}>
                My Card
              </button>
              <button type="button" onClick={() => goTo('/topup')} className={`${navLinkStyle('/topup')} block w-full text-left`}>
                Top-Up
              </button>
              <button type="button" onClick={() => goTo('/noticeboard')} className={`${navLinkStyle('/noticeboard')} block w-full text-left`}>
                Noticeboard
              </button>
              <button type="button" onClick={() => goTo('/collections')} className={`${navLinkStyle('/collections')} block w-full text-left`}>
                Collections
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => goTo('/delete-account')}
            className={`${navLinkStyle('/delete-account')} block w-full text-left`}
          >
            Account
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="block w-full text-left bg-[#F59E0B] hover:bg-[#E58F00] text-[#0B1C2C] px-4 py-2 rounded-md font-bold transition"
          >
            Logout
          </button>
        </div>
      )}
    </>
  );
}
